"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useForm = void 0;
const react_1 = require("react");
const immer_1 = require("immer");
const lodash_1 = require("lodash");
const ui_1 = require("@relymd/ui");
const fetch_1 = require("./fetch");
if (!ui_1.isRunningClientSide) {
    // Add dummy "FormData" object to global to prevent errors
    // when FormData is referenced server-side. This workaround
    // prevents RUNTIME errors so it may not be safe long term.
    // FormData is ONLY used client-side currently.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    global.FormData = class {
    };
}
const useForm = (props) => {
    var _a;
    const initialValues = (props === null || props === void 0 ? void 0 : props.initialValues) || {};
    let initialErrors = (_a = props === null || props === void 0 ? void 0 : props.initialErrors) !== null && _a !== void 0 ? _a : [];
    if (!Array.isArray(initialErrors)) {
        initialErrors = [];
    }
    const initialValuesRef = (0, react_1.useRef)(null);
    if (!initialValuesRef.current) {
        initialValuesRef.current = initialValues;
    }
    const action = props === null || props === void 0 ? void 0 : props.action;
    const method = props === null || props === void 0 ? void 0 : props.method;
    const useFormData = (props === null || props === void 0 ? void 0 : props.useFormData) || false;
    const fieldsRef = (0, react_1.useRef)({});
    const populateFormData = (0, react_1.useCallback)((values) => {
        if (!ui_1.isRunningClientSide) {
            return undefined;
        }
        const formData = new FormData();
        if (!values) {
            return formData;
        }
        const keys = Object.keys(values);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = values[key];
            formData.append(key, (0, ui_1.isString)(value) || (0, ui_1.isBlob)(value) ? value : JSON.stringify(value));
        }
        return formData;
    }, []);
    const initialFormState = {
        isSubmitting: false,
        isValidating: false,
        values: initialValues,
        formData: populateFormData(initialValues) || new FormData(),
        errors: initialErrors,
        action: {
            isComplete: false,
            isCanceled: false,
            isLoading: false,
            isOk: false,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            fetch: () => __awaiter(void 0, void 0, void 0, function* () { }),
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            cancel: () => { },
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setBody: (newBody) => { },
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setHeaders: (newHeaders) => { },
        },
    };
    const [state, setState] = (0, ui_1.useSetState)(initialFormState);
    const fetchHook = (0, fetch_1.useFetch)({
        request: action || '',
        method,
        body: useFormData ? state.formData : state.values,
    });
    const setFieldValue = (0, react_1.useCallback)((field, rawValue) => {
        const fieldRef = fieldsRef.current[field];
        // For checkboxes, handle array logic for multiple options.
        if ((fieldRef === null || fieldRef === void 0 ? void 0 : fieldRef.ref.type) === 'checkbox') {
            setState((draft) => {
                var _a, _b, _c, _d, _e, _f;
                // If the value being set is an empty array, reset the
                // field's state to an empty array and uncheck all
                // of the checkboxes.
                if (Array.isArray(rawValue)) {
                    if (rawValue.length === 0) {
                        draft.values[field] = [];
                        (_a = fieldRef.options) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
                            item.ref.checked = false;
                        });
                    }
                    else {
                        draft.values[field] = rawValue;
                        const checkboxValues = rawValue;
                        (_b = fieldRef.options) === null || _b === void 0 ? void 0 : _b.forEach((item) => {
                            if (checkboxValues.includes(item.ref.value)) {
                                item.ref.checked = true;
                            }
                            else {
                                item.ref.checked = false;
                            }
                        });
                    }
                }
                else if (typeof rawValue === 'boolean' || rawValue === 'true' || rawValue === 'false') {
                    draft.values[field] =
                        ((_d = (_c = fieldRef.options) === null || _c === void 0 ? void 0 : _c.find((item) => item.ref.value === rawValue)) === null || _d === void 0 ? void 0 : _d.ref.checked) || false;
                }
                else {
                    const checkboxChecked = (_f = (_e = fieldRef.options) === null || _e === void 0 ? void 0 : _e.find((item) => item.ref.value === rawValue)) === null || _f === void 0 ? void 0 : _f.ref.checked;
                    const checkboxValues = draft.values[field];
                    if (checkboxChecked) {
                        const existing = checkboxValues.find((item) => item === rawValue);
                        if (!existing) {
                            draft.values[field] = [...draft.values[field], rawValue];
                        }
                    }
                    else {
                        const existing = checkboxValues.find((item) => item === rawValue);
                        if (existing) {
                            draft.values[field] = draft.values[field].filter((item) => item !== rawValue);
                        }
                    }
                }
            });
        }
        else if ((fieldRef === null || fieldRef === void 0 ? void 0 : fieldRef.ref.type) === 'radio') {
            setState((draft) => {
                var _a;
                (_a = fieldRef.options) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
                    item.ref.checked = item.ref.value === rawValue;
                });
                draft.values[field] = rawValue;
            });
        }
        else {
            setState((draft) => {
                const values = Object.assign({}, draft.values);
                (0, lodash_1.set)(values, field, rawValue);
                draft.values = values;
            });
        }
    }, [setState]);
    const getFieldError = (0, react_1.useCallback)((field) => {
        if (state.errors && Array.isArray(state.errors)) {
            return state.errors.find((item) => item.field === field);
        }
        return undefined;
    }, [state.errors]);
    const handleSubmit = (0, react_1.useCallback)((onSubmit) => (event) => __awaiter(void 0, void 0, void 0, function* () {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        if (event && event.persist) {
            event.persist();
        }
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        yield onSubmit(state.values, event);
    }), [state.values]);
    const registerFieldRef = (0, react_1.useCallback)((ref, options = {}) => {
        const { name, type, value } = ref;
        const fieldRefAndOptions = Object.assign({ ref }, options);
        const fields = fieldsRef.current;
        const compareRef = (currentRef) => ui_1.isRunningClientSide && (!(0, ui_1.isHTMLElement)(ref) || currentRef === ref);
        let field = fields[name];
        if (field &&
            ((0, ui_1.isCheckboxOrRadioInput)(ref)
                ? Array.isArray(field.options) &&
                    field.options.filter(Boolean).find((option) => {
                        return value === option.ref.value && compareRef(option.ref);
                    })
                : compareRef(field.ref))) {
            fields[name] = Object.assign(Object.assign({}, field), options);
            return;
        }
        if (type) {
            field = (0, ui_1.isCheckboxOrRadioInput)(ref)
                ? Object.assign({ options: [
                        ...((field && field.options) || []).filter(Boolean),
                        {
                            ref,
                        },
                    ], ref: { type, name } }, options) : Object.assign({}, fieldRefAndOptions);
        }
        else {
            field = fieldRefAndOptions;
        }
        fields[name] = field;
    }, []);
    const registerField = (0, react_1.useCallback)((fieldOrName, options) => {
        if (ui_1.isRunningClientSide) {
            if ((0, ui_1.isString)(fieldOrName)) {
                registerFieldRef({ name: fieldOrName }, options);
            }
            else if ((0, ui_1.isObject)(fieldOrName) && !(0, ui_1.isNull)(fieldOrName) && 'name' in fieldOrName) {
                registerFieldRef(fieldOrName, options);
            }
            else {
                return (ref) => ref && registerFieldRef(ref, fieldOrName);
            }
        }
    }, [registerFieldRef]);
    const unregisterField = (0, react_1.useCallback)((name) => {
        if (ui_1.isRunningClientSide && name) {
            const fields = fieldsRef.current;
            setState((draft) => {
                var _a;
                draft.values[name] = (_a = initialValuesRef.current) === null || _a === void 0 ? void 0 : _a[name];
            });
            delete fields[name];
        }
    }, [setState]);
    const clearErrors = (0, react_1.useCallback)(() => {
        setState((draft) => {
            draft.errors = initialErrors;
        });
    }, [setState, initialErrors]);
    // When the main values (in state.values) change, populate the
    // formData object with those values.
    (0, react_1.useEffect)(() => {
        setState((draft) => {
            draft.formData = populateFormData(state.values) || new FormData();
        });
    }, [setState, populateFormData, state.values]);
    // Pull errors from the useFetch() hook and populate
    // validation errors.
    (0, react_1.useEffect)(() => {
        var _a, _b, _c;
        if ((_c = (_b = (_a = fetchHook.error) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c.items) {
            const validationErrors = fetchHook.error.data.errors.items;
            setState((draft) => {
                draft.errors = (0, immer_1.castDraft)(validationErrors.map((item) => {
                    return {
                        field: item.key,
                        message: item.message,
                    };
                }));
            });
        }
        else {
            setState((draft) => {
                draft.errors = [];
            });
        }
    }, [setState, fetchHook.error]);
    (0, react_1.useEffect)(() => {
        setState((draft) => {
            var _a;
            draft.errors = (_a = props === null || props === void 0 ? void 0 : props.initialErrors) !== null && _a !== void 0 ? _a : [];
        });
    }, [setState, props === null || props === void 0 ? void 0 : props.initialErrors]);
    return {
        isSubmitting: state.isSubmitting,
        isValidating: state.isValidating,
        formData: state.formData,
        values: state.values,
        errors: state.errors,
        action: fetchHook,
        setFieldValue,
        getFieldError,
        handleSubmit,
        registerField,
        unregisterField,
        clearErrors,
    };
};
exports.useForm = useForm;
//# sourceMappingURL=form.js.map